<template>
  <div class="flex-container">
    <div class="main-area">
      <PageTop
        title="Ürün Yönetimi"
        :educationalVideoUrl="'https://magazadestek.n11.com/egitim-detay/urun-listesi-sayfasinda-hangi-islemler-yapilabilir-22230'"
      />
      <div class="filter-area-top box">
        <CheckboxArea
          @filter-emitter="setPayload"
          @tab-click="handleTabClick"
        />
      </div>
      <div class="filter-area-bottom box">
        <Filters
          ref="filtersComponent"
          :selected-key="selectedTabIndex"
          @filter-emitter="setPayload"
        />
      </div>
      <div class="list-area box">
        <p class="table-title">
          Tüm Ürünler
        </p>
        <DataTable
          ref="productsTableRef"
          :key="activeTab"
          :payload="payloadData"
          :columns="columns"
          @row-selected="handleRowStatus"
        >
          <template #table-top-actions>
            <div class="table-top-buttons">
              <DropdownList
                :btn-text="getButtonText"
                btn-size="sm"
                :btn-disabled="!isSelectedRows"
                :drop-items="batchActionProps"
                class="bulk-dropdown"
              />
              <AppButton
                id="customize-table"
                className="product-list-secondary"
                secondary
                sm
                title="Tabloyu Özelleştir"
                @click="customizeTable"
              />
              <AppButton
                id="download-excel-btn"
                className="product-list-secondary"
                secondary
                sm
                title="Excel ile İndir"
                @click="exportExcel"
              />
            </div>
          </template>
        </DataTable>
      </div>
    </div>
    <div>
      <ExportExcelModal
        ref="exportAsExcelModalRef"
        :excel-export-content="excelExportContent"
      />
      <CustomizeTableModal
        ref="customizeTableModalRef"
        :products-table-ref="productsTableRef"
      />
      <ChangeApproveStatusModal
        ref="changeApproveStatusModalRef"
        :get-selected-rows="getSelectedRows"
        :tab-param="payloadData.tabParam"
        :product-status="productStatus"
        @success-change-approve-status="setPayload(payloadData)"
      />
      <DeleteProductModal
        ref="deleteProductModalRef"
        page-name="listing"
        :selected-products="getSelectedRows"
        :tab-param="payloadData.tabParam"
        :product-status="productStatus"
        @success-delete-product="setPayload(payloadData)"
      />
      <SelectCargoTemplateModal
        ref="selectCargoTemplateModalRef"
        :shipment-templates="shipmentTemplates"
        :get-selected-rows="getSelectedRows"
        @success-update-cargo-template="setPayload(payloadData)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { AppButton, DropdownList } from '@n11/so-ui-components';
import {
  ToastMessageTypes,
  ToastSuccessMessage,
} from '@/utils/enum/toastMessageTypes';
import { columnService } from '@/components/Table/columnService';
import DataTable from '@/components/Table/DataTable';
import Filters from '@/components/Filters/Filters';
import CheckboxArea from '@/components/CheckboxArea/CheckboxArea';
import PageTop from '@/components/Page/PageTop.vue';
import SelectCargoTemplateModal from './partials/SelectCargoTemplateModal.vue';
import DeleteProductModal from '@/components/common/DeleteProductModal/DeleteProductModal.vue';
import ChangeApproveStatusModal from './partials/ChangeApproveStatusModal';
import ExportExcelModal from './partials/ExportExcelModal';
import CustomizeTableModal from './partials/CustomizeTableModal';
import { NEW_BULK_PRODUCT_MANAGEMENT_DESIGN_ENABLE } from '@/utils/enum/configs';

const store = useStore();
const route = useRoute();
const router = useRouter();
const productsTableRef = ref();
const exportAsExcelModalRef = ref();
const customizeTableModalRef = ref();
const changeApproveStatusModalRef = ref();
const deleteProductModalRef = ref();
const selectCargoTemplateModalRef = ref();
const productStatus = ref([]);

const shipmentTemplates = ref([]);
const requestObject = ref({});
const selectedTabIndex = ref(0);
const filtersComponent = ref(null);
const excelExportContent = ref(
  'Dosyanın durumunu <b>Toplu Ürün Yönetimi</b> sayfasındaki dosya indirme geçmişinden takip edebilirsiniz.',
);
const payloadData = ref({
  productTitle: null,
  stockCode: null,
  pimsId: null,
  gtin: null,
  modelCode: null,
  categoryIds: null,
  brandName: null,
  pageRequest: {
    sortOrders: null,
    pageSize: '10',
  },
  after: null,
  before: null,
  jumpPage: null,
  goLast: false,
});

const isBulkConfigEnable = computed(
  () =>
    store.getters["common/getConfigList"][
      NEW_BULK_PRODUCT_MANAGEMENT_DESIGN_ENABLE
    ]
);
const getIsPriceAreaErrorMessage = computed(
  () => store.getters["products/getIsPriceAreaErrorMessage"]
);
const getIsProductUpdate = computed(
  () => store.getters["products/getIsProductUpdate"]
);
const getSelectedRows = computed(() =>
  productsTableRef?.value?.getSelectedRows()
);
const getSelectedRowsLength = computed(
  () => Object.keys(getSelectedRows.value ?? {}).length
);
const getButtonText = computed(
  () =>
    `Toplu İşlemler ${
      getSelectedRowsLength.value > 0
        ? "(" + getSelectedRowsLength.value + ")"
        : ""
    }`
);
const isSelectedRows = computed(
  () => Object.keys(getSelectedRows.value ?? {}).length
);
const activeTab = computed(
  () => store.getters["products/getProductListActiveTab"]
);
const batchActionProps = computed(() => [
  {
    item: 'Onay Durumunu Değiştir',
    onClick: () =>
      changeApproveStatusModalRef?.value?.openChangeApproveStatusModal(),
    disable: !isSelectedRows.value,
  },
  {
    item: 'Sil',
    onClick: () => deleteProductModalRef?.value?.openDeleteModal(),
    disable: !isSelectedRows.value,
  },
  {
    item: 'Kargo Şablonu Seç',
    onClick: () =>
      selectCargoTemplateModalRef?.value?.openSelectCargoTemplateModal(),
    disable: !isSelectedRows.value || (shipmentTemplates.value ?? []).length === 0,
  },
]);

const handleTabClick = (index) => {
  selectedTabIndex.value = index;
  if (filtersComponent.value && filtersComponent.value.clearFilters) {
    filtersComponent.value.clearFilters();
  }
  if (productsTableRef.value && productsTableRef.value.selectedStatuses) {
    productsTableRef.value.selectedStatuses = [];
  }
};

const handleRowStatus = (status) => {
  productStatus.value = status;
};

const setPayload = (p) => {
  requestObject.value = p;
  productsTableRef?.value?.table.resetRowSelection();
  payloadData.value = {
    ...payloadData.value,
    ...p,
  };
  if (productsTableRef?.value) {
    productsTableRef.value.selectedStatuses = []; // silme veya onay durum degisişikliği yapılınca secili statulerin temizlenmesi
  }
};

const successMessage = (inputId) => {
  if (inputId.includes('listPrice'))
    return ToastSuccessMessage.PRICE_UPDATE_SUCCESS;
  if (inputId.includes('salesPrice'))
    return ToastSuccessMessage.N11_PRICE_UPDATE_SUCCESS;
  return ToastSuccessMessage.STOCK_UPDATE_SUCCESS;
};

const getShipmentTemplates = async () => {
  try {
    const res = await store.dispatch("templates/getShipmentTemplates");
    shipmentTemplates.value = res.data?.shipmentTemplates;
  } catch (error) {
    console.error('error: ', error);
  }
};

const exportExcel = async () => {
  let response = null;

  if (isBulkConfigEnable.value === "true") {
    response = await store.dispatch(
      "products/requestExportExcelNew",
      payloadData.value
    );
  } else {
    response = await store.dispatch(
      "products/requestExportExcel",
      payloadData.value
    );
  }

  excelExportContent.value = response.message;
  exportAsExcelModalRef?.value?.openExportasExcelModal();
};

const customizeTable = () =>
  customizeTableModalRef.value?.openCustomizeTableModal();

const refreshCallback = () => {
  setPayload(payloadData.value);
};

const columns = computed(() => columnService({
  store,
  router,
  refreshCallback,
  activeTab: activeTab.value,
}).value);

watch(getIsPriceAreaErrorMessage, (newPriceErrorMessage) => {
  if (newPriceErrorMessage) {
    store.commit("common/SET_TOASTER_DATA", {
      type: ToastMessageTypes.ERROR,
      description: newPriceErrorMessage,
    });
    store.commit('products/SET_IS_PRICE_AREA_STATUS', null);
    store.commit('products/SET_IS_PRICE_AREA_ERROR_MESSAGE', '');
  }
});

watch(getIsProductUpdate, (newUpdateVal) => {
  if (newUpdateVal) {
    setPayload(payloadData.value);
    store.commit("common/SET_TOASTER_DATA", {
      type: ToastMessageTypes.SUCCESS,
      description: successMessage(store.getters["products/getActivePriceAreaInput"]),
    });
    store.commit("products/SET_IS_PRICE_AREA_UPDATE", true);
  }
});

watch(
  () => route.query.tab,
  (newTab) => {
    if (newTab) {
      store.dispatch('products/setProductListActiveTab', newTab);
    }
  },
);

onMounted(() => {
  const tabParam = route.query.tab;
  payloadData.value = {
    ...payloadData.value,
    tabParam,
    // TODO: Atilla - productStatus: [STATUS.INAPPROVAL]
    // Backend geliştirme tamamlanana kadar in-approval status gönderilmeyecek
  };

  if (tabParam) {
    store.dispatch('products/setProductListActiveTab', tabParam);
  }

  getShipmentTemplates();
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables';
@import '@/assets/styles/utilities';

.main-area {
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  margin-top: 0;
  margin-bottom: 32px;
  gap: 16px;
}

.flex-container {
  display: flex;
  width: 100%;
  margin-top: 16px;
}

.box {
  width: 100%;
  padding: 16px 24px;
  background: $colorN00;
  border-radius: 8px;
}

.table-title {
  padding: 8px 16px 16px 0;
  color: var(--Neutral---Background-N80, #242424);
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  letter-spacing: -0.05px;
  line-height: 20px;
}

.table-top-buttons {
  position: relative;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;

  .bulk-dropdown {
    z-index: 2;
  }

  #download-excel-btn {
    width: auto;
  }
}

.filter-area-top {
  width: auto;
  height: auto;
  margin-bottom: -16px;
  border-radius: 8px 8px 0 0;
  border-bottom: 1px solid $colorN30;
}

.filter-area-bottom {
  border-radius:  0 0 8px 8px;
}
</style>
